import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Form, Select, Input, Button, message, Spin, Tag } from 'antd';
import { fetchChatbotAgents } from './api/fetchChatbotAgents';
import { fetchChatbotConfig } from './api/fetchChatbotConfig';
import { postChatbotConfig } from './api/postChatbotConfig';
import { getAiChatApiBase } from './config';
import {
  getConnectionSettings,
  setConnectionSettings,
} from './persistence/connectionSettings';

/** API tokens for POST `visibility` (comma-separated). */
const VISIBILITY_OPTIONS = [
  { value: 'admin', label: 'Admins' },
  { value: 'internal', label: 'Internal' },
  { value: 'external', label: 'External' },
  { value: 'allmembers', label: 'All project members' },
];

/** Always-on visibility token; the corresponding tag is rendered non-closable. */
const LOCKED_VISIBILITY = 'admin';

function renderVisibilityTag({ label, value, closable, onClose }) {
  const isLocked = value === LOCKED_VISIBILITY;
  const handleClose = (e) => {
    e?.preventDefault?.();
    if (!isLocked) {
      onClose?.(e);
    }
  };
  return (
    <Tag
      className={`ddv-ai-chat__config-visibility-tag${
        isLocked ? ' ddv-ai-chat__config-visibility-tag--locked' : ''
      }`}
      closable={isLocked ? false : closable}
      onClose={isLocked ? undefined : handleClose}
      style={{ marginInlineEnd: 4 }}
    >
      {label}
    </Tag>
  );
}

function ensureLockedVisibility(arr) {
  const base = Array.isArray(arr) ? arr.filter(Boolean) : [];
  if (base.includes(LOCKED_VISIBILITY)) {
    return base;
  }
  return [LOCKED_VISIBILITY, ...base];
}

/** Default welcome template (Markdown); empty field in config uses the built-in greeting instead. */
const INTRO_MESSAGE_EXAMPLE_PLACEHOLDER = `Hi **{{userFirstName}}**, I'm **{{agentName}}**, your chat assistant for this module. Ask a question.`;

/** Above `.ddv-ai-chat__panel` (z-index 2000) so the menu is not hidden behind the panel. */
const SELECT_DROPDOWN_Z_INDEX = 3000;

const selectDropdownProps = {
  getPopupContainer: () => document.body,
  dropdownStyle: { zIndex: SELECT_DROPDOWN_Z_INDEX },
};

function normalizeVisibilityToken(t) {
  const x = String(t).trim().toLowerCase();
  if (x === 'admins') {
    return 'admin';
  }
  return x;
}

function parseVisibilityFromApi(v) {
  if (Array.isArray(v)) {
    return v.map((x) => normalizeVisibilityToken(x)).filter(Boolean);
  }
  if (typeof v === 'string' && v.trim()) {
    return v
      .split(',')
      .map((p) => normalizeVisibilityToken(p))
      .filter(Boolean);
  }
  return [];
}

function visibilityStringToFormArray(v) {
  if (Array.isArray(v)) {
    return ensureLockedVisibility(
      v.map((x) => normalizeVisibilityToken(x)).filter(Boolean),
    );
  }
  const s = String(v || '').trim();
  if (!s) {
    return [LOCKED_VISIBILITY];
  }
  return ensureLockedVisibility(
    s
      .split(',')
      .map((p) => normalizeVisibilityToken(p))
      .filter(Boolean),
  );
}

/**
 * @param {string} savedAgent
 * @param {Array<{ id: string }>} agents
 * @returns {string}
 */
function resolveAgentFieldValue(savedAgent, agents) {
  if (!agents.length) {
    return savedAgent;
  }
  const idSet = new Set(agents.map((a) => a.id));
  if (savedAgent && idSet.has(savedAgent)) {
    return savedAgent;
  }
  return agents[0].id;
}

/**
 * @param {import('./api/fetchChatbotConfig').ChatbotConfigDto | null} serverConfig
 * @param {Array<{ id: string }>} agents
 */
function mapServerToForm(serverConfig, agents) {
  const agentId = serverConfig?.agentId || serverConfig?.agent?.id || '';
  const agent = resolveAgentFieldValue(agentId, agents);
  const visibility = ensureLockedVisibility(
    parseVisibilityFromApi(serverConfig?.visibility),
  );
  return {
    agent,
    visibility,
    sqlEndpointUrl: serverConfig?.lakehouseSQLEndpoint ?? '',
    lakehouseName: serverConfig?.lakehouseName ?? '',
    customInstructions: serverConfig?.customInstruction ?? '',
    introMessage: serverConfig?.introMessage ?? '',
  };
}

/**
 * @param {{
 *   projectId: string | null,
 *   moduleId?: string | null,
 *   onClose: () => void,
 *   onSaved?: (payload?: { introMessage?: string }) => void,
 * }} props
 */
export default function AiChatConfigPane({
  projectId,
  moduleId = null,
  onClose,
  onSaved,
}) {
  const [form] = Form.useForm();
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(true);
  const [agentsError, setAgentsError] = useState(null);
  const [serverConfig, setServerConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [configError, setConfigError] = useState(null);
  const [saving, setSaving] = useState(false);
  const baselineRef = useRef(null);

  const useServerConfig = Boolean(moduleId && getAiChatApiBase());

  useEffect(() => {
    let cancelled = false;
    setAgentsLoading(true);
    setAgentsError(null);

    if (!getAiChatApiBase()) {
      setAgents([]);
      setAgentsLoading(false);
      return undefined;
    }

    (async () => {
      try {
        const list = await fetchChatbotAgents();
        if (!cancelled) {
          setAgents(list);
        }
      } catch (e) {
        if (!cancelled) {
          setAgents([]);
          setAgentsError(
            typeof e?.message === 'string'
              ? e.message
              : 'Could not load agents.',
          );
        }
      } finally {
        if (!cancelled) {
          setAgentsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!moduleId || !getAiChatApiBase()) {
      setServerConfig(null);
      setConfigLoading(false);
      setConfigError(null);
      return undefined;
    }

    let cancelled = false;
    setConfigLoading(true);
    setConfigError(null);

    fetchChatbotConfig(moduleId)
      .then((data) => {
        if (!cancelled) {
          setServerConfig(data);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setServerConfig(null);
          setConfigError(
            typeof e?.message === 'string'
              ? e.message
              : 'Could not load chatbot config.',
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setConfigLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [moduleId]);

  useEffect(() => {
    if (agentsLoading) {
      return;
    }
    if (useServerConfig && configLoading) {
      return;
    }

    let next;
    if (useServerConfig) {
      next = mapServerToForm(serverConfig, agents);
    } else {
      const saved = getConnectionSettings(projectId);
      next = {
        agent: resolveAgentFieldValue(saved.agent, agents),
        visibility: visibilityStringToFormArray(saved.visibility),
        sqlEndpointUrl: saved.sqlEndpointUrl || '',
        lakehouseName: saved.lakehouseName || '',
        customInstructions: saved.customInstructions || '',
        introMessage: saved.introMessage || '',
      };
    }

    baselineRef.current = JSON.parse(JSON.stringify(next));
    form.setFieldsValue(next);
  }, [
    agentsLoading,
    configLoading,
    useServerConfig,
    serverConfig,
    projectId,
    agents,
    form,
    moduleId,
  ]);

  const agentOptions = useMemo(
    () => agents.map((a) => ({ value: a.id, label: a.displayName })),
    [agents],
  );

  const agentRules = useMemo(
    () =>
      agents.length > 0 ? [{ required: true, message: 'Select an agent' }] : [],
    [agents.length],
  );

  const visibilityRules = useMemo(
    () => [
      {
        required: true,
        type: 'array',
        min: 1,
        message: 'Select at least one visibility option',
      },
    ],
    [],
  );

  const onSave = () => {
    form
      .validateFields()
      .then(async (values) => {
        const rawVisibility = Array.isArray(values.visibility)
          ? values.visibility
          : String(values.visibility ?? '')
              .split(',')
              .map((p) => p.trim())
              .filter(Boolean);
        const dedupedVisibility = Array.from(
          new Set(ensureLockedVisibility(rawVisibility)),
        );
        values.visibility = dedupedVisibility;
        const visStr = dedupedVisibility.join(',');

        if (useServerConfig && projectId && moduleId) {
          setSaving(true);
          try {
            await postChatbotConfig({
              projectId,
              moduleId,
              agentId: values.agent,
              lakehouseName: values.lakehouseName ?? '',
              lakehouseSQLEndpoint: values.sqlEndpointUrl ?? '',
              customInstruction: values.customInstructions ?? '',
              introMessage: values.introMessage ?? '',
              visibility: visStr,
            });
            setConnectionSettings(projectId, {
              ...values,
              visibility: visStr,
            });
            message.success('Chatbot configuration saved.');
            baselineRef.current = JSON.parse(
              JSON.stringify({
                ...values,
                visibility: values.visibility,
              }),
            );
            onSaved?.({ introMessage: values.introMessage ?? '' });
            onClose();
          } catch (e) {
            const errMsg =
              e?.response?.data?.message ||
              e?.message ||
              'Could not save configuration.';
            message.error(typeof errMsg === 'string' ? errMsg : String(errMsg));
          } finally {
            setSaving(false);
          }
          return;
        }

        if (projectId) {
          setConnectionSettings(projectId, {
            ...values,
            visibility: visStr,
          });
        }
        onSaved?.({ introMessage: values.introMessage ?? '' });
        onClose();
      })
      .catch(() => {});
  };

  const onCancel = () => {
    if (baselineRef.current) {
      form.setFieldsValue(JSON.parse(JSON.stringify(baselineRef.current)));
    }
    onClose();
  };

  const showBaseHint = !getAiChatApiBase();
  const selectDisabled = agentsLoading || agents.length === 0;
  const showModuleHint = getAiChatApiBase() && !moduleId && !configLoading;

  const formBusy = agentsLoading || (useServerConfig && configLoading);

  return (
    <div className="ddv-ai-chat__config">
      <div className="ddv-ai-chat__config-scroll">
        <div className="ddv-ai-chat__config-intro">
          <h2 className="ddv-ai-chat__config-title">Configuration</h2>
          <p className="ddv-ai-chat__config-subtitle">
            Customize connection settings and agent behavior. Saving will apply
            changes to the next conversation.
          </p>
        </div>

        {showBaseHint ? (
          <Alert
            type="info"
            showIcon
            className="ddv-ai-chat__config-alert"
            message="Set REACT_APP_AI_CHAT_API_BASE (e.g. http://localhost:3001/api/v1) to load agents from the chatbot API."
          />
        ) : null}

        {showModuleHint ? (
          <Alert
            type="info"
            showIcon
            className="ddv-ai-chat__config-alert"
            message="Server configuration is tied to the current module. Open AI Chat from a project module page (or a link that includes module context) to load and save settings to the server; otherwise settings use this browser only."
          />
        ) : null}

        {configError ? (
          <Alert
            type="warning"
            showIcon
            className="ddv-ai-chat__config-alert"
            message={configError}
          />
        ) : null}

        {agentsError ? (
          <Alert
            type="warning"
            showIcon
            className="ddv-ai-chat__config-alert"
            message={agentsError}
          />
        ) : null}

        {!agentsLoading &&
        !agentsError &&
        getAiChatApiBase() &&
        agents.length === 0 ? (
          <Alert
            type="info"
            showIcon
            className="ddv-ai-chat__config-alert"
            message="No agents returned from the server."
          />
        ) : null}

        <Spin spinning={formBusy} className="ddv-ai-chat__config-spin">
          <Form
            form={form}
            layout="vertical"
            className="ddv-ai-chat__config-form"
            requiredMark={false}
          >
            <Form.Item label="Agent" name="agent" rules={agentRules}>
              <Select
                {...selectDropdownProps}
                className="ddv-ai-chat__config-select"
                dropdownClassName="ddv-ai-chat-config-dropdown"
                loading={agentsLoading}
                disabled={selectDisabled}
                placeholder={
                  agentsLoading
                    ? 'Loading agents…'
                    : agents.length === 0
                    ? 'No agents available'
                    : 'Select an agent'
                }
                options={agentOptions}
              />
            </Form.Item>

            <Form.Item
              className="ddv-ai-chat__config-item--with-hint"
              label={
                <span className="ddv-ai-chat__config-field-label">
                  Visibility
                  <span className="ddv-ai-chat__config-hint">
                    Admins access is always enabled and cannot be removed.
                  </span>
                </span>
              }
              name="visibility"
              rules={visibilityRules}
            >
              <Select
                {...selectDropdownProps}
                mode="multiple"
                className="ddv-ai-chat__config-select"
                dropdownClassName="ddv-ai-chat-config-dropdown"
                placeholder="Select visibility"
                tagRender={renderVisibilityTag}
                onChange={(next) => {
                  const arr = Array.isArray(next) ? next : [];
                  if (!arr.includes(LOCKED_VISIBILITY)) {
                    form.setFieldsValue({
                      visibility: ensureLockedVisibility(arr),
                    });
                  }
                }}
                options={VISIBILITY_OPTIONS.map((o) => ({
                  value: o.value,
                  label: o.label,
                  disabled: o.value === LOCKED_VISIBILITY,
                }))}
              />
            </Form.Item>

            <Form.Item label="SQL Endpoint URL" name="sqlEndpointUrl">
              <Input
                className="ddv-ai-chat__config-input"
                placeholder="Endpoint identifier or URL"
                autoComplete="off"
              />
            </Form.Item>

            <Form.Item label="Lakehouse Name" name="lakehouseName">
              <Input
                className="ddv-ai-chat__config-input"
                placeholder="Lakehouse name"
                autoComplete="off"
              />
            </Form.Item>

            <Form.Item
              className="ddv-ai-chat__config-item--with-hint"
              label={
                <span className="ddv-ai-chat__config-field-label">
                  Greeting
                  <span className="ddv-ai-chat__config-hint">
                    Shown when users start a new chat. Leave empty for default.
                    Supports Markdown and the variables
                    <code>{'{{userFirstName}}'}</code> and{' '}
                    <code>{'{{agentName}}'}</code>
                  </span>
                </span>
              }
              name="introMessage"
            >
              <Input.TextArea
                className="ddv-ai-chat__config-textarea ddv-ai-chat__config-input"
                placeholder={INTRO_MESSAGE_EXAMPLE_PLACEHOLDER}
                rows={4}
              />
            </Form.Item>

            <Form.Item
              className="ddv-ai-chat__config-item--with-hint"
              label={
                <span className="ddv-ai-chat__config-field-label">
                  Custom Instructions
                  <span className="ddv-ai-chat__config-hint">
                    Additional developer instructions included with each new
                    conversation.
                  </span>
                </span>
              }
              name="customInstructions"
            >
              <Input.TextArea
                className="ddv-ai-chat__config-textarea ddv-ai-chat__config-input ddv-ai-chat__config-textarea--instructions"
                placeholder="[Placeholder]"
                rows={8}
              />
            </Form.Item>
          </Form>
        </Spin>
      </div>

      <div className="ddv-ai-chat__config-footer">
        <div className="ddv-ai-chat__config-actions">
          <Button
            className="ddv-ai-chat__config-cancel"
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            className="ddv-ai-chat__config-save"
            onClick={onSave}
            loading={saving}
            disabled={formBusy}
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}
